import { useFormikContext } from 'formik';
import React from 'react';

import SafeHtmlOnly from 'App/components/SafeHtmlOnly/SafeHtmlOnly';
import { dateOnlySlashesFormat, formatUtcDate } from 'App/services/DateService';
import { stripAllHtml, stripCurlyTags } from 'App/utils';
import { branchSettingsPropType, patientPropType } from 'Shared/types';
import { questionPropType } from '../types';

function isQuestionOrDescendentsEhr(question, values) {
    const children = values[question.questionId]?.children || [];
    return (
        question.attributes.ehr_encounter_note ||
        children.reduce((agg, child) => {
            return agg || isQuestionOrDescendentsEhr(child, values);
        }, false)
    );
}

const EhrEncounterNoteQuestion = ({ question, patient, branchSettings }) => {
    const { values } = useFormikContext();
    const questionValue = values[question.questionId];

    if (!isQuestionOrDescendentsEhr(question, values)) {
        return null;
    }

    if (branchSettings.ehrNoteHideUnanswered && (!questionValue?.response || questionValue?.response.length === 0)) {
        return null;
    }

    function getFormattedResponse(response) {
        if (['checkbox', 'radio', 'select'].includes(question.type)) {
            const fullAnswer = question.answers[response?.id || response];
            return fullAnswer?.option;
        }
        if (question.type === 'date') {
            return formatUtcDate({ date: response, format: dateOnlySlashesFormat });
        }
        return response;
    }

    const allResponses = [].concat(questionValue?.response);
    return (
        <ul data-testid={`ehr-question-${question.questionId}`}>
            <li>
                <strong>{stripAllHtml(stripCurlyTags(question.compactQuestion || question.question))}</strong>
            </li>
            <ul>
                {!allResponses.length ? (
                    <li>{'<unanswered>'}</li>
                ) : (
                    allResponses.map((response) => {
                        const formattedResponse = getFormattedResponse(response);
                        return (
                            <li
                                key={`${question.questionId}-${response}`}
                                data-testid={`ehr-question-${question.questionId}-${response}`}
                            >
                                {formattedResponse ? (
                                    <SafeHtmlOnly>{stripCurlyTags(formattedResponse)}</SafeHtmlOnly>
                                ) : (
                                    '<unanswered>'
                                )}
                                {(questionValue?.children || [])
                                    .filter((child) => {
                                        return question.type !== 'checkbox' || child.parentAnswerId === response;
                                    })
                                    .sort((a, b) => a.attributes.position - b.attributes.position)
                                    .map((child) => (
                                        <EhrEncounterNoteQuestion
                                            key={child.questionId}
                                            question={child}
                                            patient={patient}
                                            branchSettings={branchSettings}
                                        />
                                    ))}
                            </li>
                        );
                    })
                )}
            </ul>
        </ul>
    );
};

EhrEncounterNoteQuestion.propTypes = {
    question: questionPropType.isRequired,
    patient: patientPropType.isRequired,
    branchSettings: branchSettingsPropType.isRequired,
};

export default EhrEncounterNoteQuestion;
